import { RootState } from "../../store";
import { Organization, OrganizationKeyName } from "../../model/Organization";
import { AuthFailReason } from "../../model/AuthFailReason";
import * as _ from "lodash";
import { Roles } from "../../model/RolesEnum";

export const isUserAuthenticated = (state: RootState): boolean =>
  state.auth.isAuthenticated;

export const isAuthenticating = (state: RootState): boolean =>
  state.auth.isAuthenticating;

export const isSelectingOrganization = (state: RootState): boolean =>
  state.auth.isSelectingOrganization;

export const selectedOrganization = (
  state: RootState
): Organization | undefined => {
  return state.auth.selectedOrganization;
};

export const isAuthenticatedForChosenOrganization = (
  state: RootState,
  organizationKeyName: string
) => {
  return state.auth.organizations.some(
    (o) => o.keyName === organizationKeyName
  );
};

export const isSelectedOrganizationAdmins = (state: RootState): boolean => {
  return (state.auth.selectedOrganization || ({} as any)).keyName === "admins";
};

export const hasSelectedOrganization = (state: RootState): boolean => {
  return !!state.auth.selectedOrganization;
};

export const selectedOrganizationKeyName = (
  state: RootState
): OrganizationKeyName => {
  const org = selectedOrganization(state);
  if (org) {
    return org.keyName;
  }

  return "";
};

export const selectedOrganizationStartOfDay = (
  state: RootState
): number[] | undefined => {
  const org = selectedOrganization(state);
  if (org) {
    const startOfDay = org.startOfDay;
    const parts = startOfDay.split(":");
    if (parts.length === 3) {
      return parts.map((x) => parseInt(x, 10));
    }
  }

  return undefined;
};

export const hasErrorWrongUsernameOrPassword = (state: RootState): boolean => {
  if (state.auth.errors.length === 0) {
    return false;
  }

  const index = state.auth.errors.findIndex(
    (x) => x.code === AuthFailReason.UnknownUserOrWrongPassword
  );

  return index !== -1;
};

export const accessToken = (state: RootState): string =>
  state.auth.accessToken || "";

export const name = (state: RootState) =>
  state.auth.me ? state.auth.me.fullname : "Anonymous";

export const organizationByKeyName = (
  state: RootState,
  organizationKeyName: string
) => {
  return state.auth.organizations.find(
    (o) => o.keyName === organizationKeyName
  );
};

export const organizations = (state: RootState) => state.auth.organizations;

export const defaultOrganization = (state: RootState) => {
  return organizations(state).find((o) => o.isDefault);
};

const hasRole = (state: RootState, role: string): boolean => {
  if (_.isArray(state.auth.roles)) {
    return !!_.find(state.auth.roles, (x) => x === role);
  }

  return false;
};

export const hasAccessToAnalytics = (state: RootState): boolean => {
  return hasRole(state, Roles.All) || hasRole(state, Roles.Analytics);
};

export const hasAccessToBeacons = (state: RootState): boolean => {
  return hasRole(state, Roles.All) || hasRole(state, Roles.Beacons);
};

export const hasAccessToUsers = (state: RootState): boolean => {
  return hasRole(state, Roles.All) || hasRole(state, Roles.Users);
};

export const hasAccessToVenues = (state: RootState): boolean => {
  return hasRole(state, Roles.All) || hasRole(state, Roles.Restaurants);
};

export const hasAccessToPromoCodes = (state: RootState): boolean => {
  return hasRole(state, Roles.All) || hasRole(state, Roles.PromoCodes);
};

export const hasAccessToPartnerships = (state: RootState): boolean => {
  return hasRole(state, Roles.All) || hasRole(state, Roles.Partnerships);
};

export const hasAccessToUiSettings = (state: RootState): boolean => {
  return hasRole(state, Roles.All) || hasRole(state, Roles.UiSettings);
};

export const hasAccessToEmailTemplates = (state: RootState): boolean => {
  return hasRole(state, Roles.All) || hasRole(state, Roles.EmailTemplates);
};

// Until we are completely done with frontend and backend only users with specific roles can access. Purposely excluding super admins aswell
// TODO: Remove once frontend and backend is ready
export const hasAccessToMenus = (state: RootState): boolean => {
  return hasRole(state, Roles.Menus);
};

export const hasAccessToAdmins = (state: RootState): boolean => {
  return (
    hasRole(state, Roles.All) ||
    hasRole(state, Roles.ManageAdminsInOrg) ||
    hasRole(state, Roles.SuperAdmin)
  );
};

export const hasAccessToMemberCardTypes = (state: RootState): boolean => {
  return hasRole(state, Roles.All) || hasRole(state, Roles.MemberCardTypes);
};

export const hasAccessToShops = (state: RootState): boolean => {
  return hasRole(state, Roles.All) || hasRole(state, Roles.Shop);
};

export const hasAccessToIntegrations = (state: RootState): boolean => {
  return hasRole(state, Roles.Integrations) || hasRole(state, Roles.All);
};

export const hasAccessToOrganizations = (state: RootState): boolean => {
  return (
    hasRole(state, Roles.All) ||
    hasRole(state, Roles.SuperAdmin) ||
    hasRole(state, Roles.ManageOrg)
  );
};

export const hasAccessToVoucherStacks = (state: RootState): boolean => {
  return hasRole(state, Roles.All) || hasRole(state, Roles.VoucherStacks);
};

export const hasAccessToChallenges = (state: RootState): boolean => {
  return hasRole(state, Roles.All) || hasRole(state, Roles.Challenges);
};

export const isSuperAdmin = (state: RootState): boolean => {
  return hasRole(state, Roles.SuperAdmin);
};

export const hasAccessToDeliveryRules = (state: RootState): boolean => {
  return hasRole(state, Roles.All) || hasRole(state, Roles.DeliveryRules);
};
